import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";

import { authGuard } from "@/constants/plugins/auth";

const routes: Array<RouteRecordRaw> = [
	{
		path: "/auth/login",
		name: "AuthLogin",
		component: () => import("@/views/auth/Login.vue"),
		meta: {
			requiresGuest: true,
			title: "Sign in",
		},
	},
	{
		path: "/auth/forgot-password",
		name: "AuthForgotPassword",
		component: () => import("@/views/auth/ForgotPassword.vue"),
		meta: {
			requiresGuest: true,
			title: "Forgot password",
		},
	},
	{
		path: "/auth/reset-password",
		name: "AuthResetPassword",
		component: () => import("@/views/auth/ResetPassword.vue"),
		meta: {
			requiresGuest: true,
			title: "Reset your password",
		},
	},
	{
		path: "/auth/logout",
		name: "AuthLogout",
		component: () => import("@/views/auth/Logout.vue"),
		meta: {
			title: "Sign out",
		},
	},
	{
		path: "/auth/register",
		name: "AuthRegister",
		component: () => import("@/views/auth/Register.vue"),
		meta: {
			title: "Register",
		},
	},
	{
		path: "/auth/admin",
		name: "AuthAdmin",
		component: () => import("@/views/auth/Admin.vue"),
		meta: {
			title: "Admin",
		},
	},
	{
		path: "/",
		component: () => import("@/layouts/AppLayout.vue"),
		meta: {
			requiresAuth: true,
		},
		children: [
			{
				path: "/",
				name: "Dashboard",
				component: () => import("@/views/Dashboard.vue"),
				redirect: { name: "DashboardAudience" },
				meta: {
					// requiresOnboarding: true,
					navCurrent: "dashboard",
					title: "Live Audience",
				},
				children: [
					{
						path: "/live",
						name: "DashboardAudience",
						component: () => import("@/views/DashboardAudience.vue"),
						meta: {
							navCurrent: "dashboard",
							title: "Live Audience",
						},
					},
				],
			},
			{
				path: "/statistics",
				name: "Statistics",
				component: () => import("@/views/Statistics.vue"),
				redirect: "/statistics/audience",
				meta: {
					// requiresOnboarding: true,
					navCurrent: "statistics",
					title: "Statistics",
				},
				children: [
					{
						path: "/statistics/audience",
						name: "StatisticsAudience",
						component: () => import("@/views/StatisticsAudience.vue"),
						meta: {
							navCurrent: "statistics",
							title: "Statistics",
							tab: "audience",
						},
					},
					{
						path: "/statistics/tlh",
						name: "StatisticsTLH",
						component: () => import("@/views/StatisticsTLH.vue"),
						meta: {
							navCurrent: "statistics",
							title: "Statistics",
							tab: "tlh",
						},
					},
					{
						path: "/statistics/bandwidth",
						name: "StatisticsBandwidth",
						component: () => import("@/views/StatisticsBandwidth.vue"),
						meta: {
							navCurrent: "statistics",
							title: "Statistics",
							tab: "bandwidth",
						},
					},
					{
						path: "/statistics/revenue",
						name: "StatisticsRevenue",
						component: () => import("@/views/StatisticsRevenue.vue"),
						meta: {
							navCurrent: "statistics",
							title: "Statistics",
							tab: "revenue",
						},
					},
				],
			},
			{
				path: "/stations/create",
				component: () => import("@/views/StationCreate.vue"),
				name: "StationCreate",
				meta: {
					navCurrent: "stations",
					title: "Create a Station",
				},
			},
			{
				path: "/stations",
				name: "StationList",
				component: () => import("@/views/StationList.vue"),
				meta: {
					// requiresOnboarding: true,
					navCurrent: "stations",
					title: "Stations",
				},
			},
			{
				path: "/stations/:station",
				component: () => import("@/views/Station.vue"),
				meta: {
					navCurrent: "stations",
					title: "Station Settings",
				},
				children: [
					{ path: "", redirect: { name: "Station" } },
					{
						path: "info",
						name: "Station",
						component: () => import("@/views/StationDetails.vue"),
						meta: {
							title: "Station Settings > Details",
						},
					},
					{
						path: "settings",
						name: "StationSettings",
						component: () => import("@/views/StationSettings.vue"),
						meta: {
							title: "Station Settings > Encocer Settings",
						},
					},
					{
						path: "streams",
						name: "StationStreams",
						component: () => import("@/views/StationStreams.vue"),
						meta: {
							title: "Station Settings > Streams",
						},
					},
					{
						path: "monetization",
						name: "StationMonetization",
						component: () => import("@/views/StationMonetization.vue"),
						meta: {
							title: "Station Settings > Monetization",
						},
					},
					{
						path: "export",
						name: "StationExport",
						component: () => import("@/views/StationExport.vue"),
						meta: {
							title: "Station Settings > Export Player",
						},
					},
				],
			},
			{
				path: "/profile",
				component: () => import("@/views/Profile.vue"),
				meta: {
					navCurrent: "profile",
					title: "Profile",
				},
				children: [
					{ path: "", redirect: { name: "Profile" } },
					{
						path: "/profile",
						name: "Profile",
						component: () => import("@/views/AccountDetails.vue"),
						meta: {
							title: "Account Settings > Personal Information",
						},
					},
					{
						path: "/profile/preferences",
						name: "ProfilePreferences",
						component: () => import("@/views/AccountPreferences.vue"),
						meta: {
							title: "Account Settings > Preferences",
						},
					},
				],
			},
			{
				path: "/onboarding",
				name: "Onboarding",
				component: () => import("@/views/Onboarding.vue"),
				meta: {
					requiresAuth: true,
					title: "Onboarding",
				},
			},
		],
	},
	{
		path: "/403",
		name: "NotAuthorized",
		component: () => import("@/views/403.vue"),
		props: {
			backTo: { name: "Dashboard" },
		},
		meta: {
			title: "403 - Unauthorized",
		},
	},
	{
		path: "/500",
		name: "ServerError",
		component: () => import("@/views/500.vue"),
		meta: {
			title: "500 - Server Error",
		},
	},
	{
		path: "/:pathMatch(.*)*",
		name: "NotFound",
		component: () => import("@/views/404.vue"),
		props: {
			backTo: { name: "Dashboard" },
		},
		meta: {
			title: "404 - Not Found",
		},
	},
];

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	scrollBehavior() {
		return {
			top: 0,
		};
	},
	routes: routes,
});

router.beforeEach(authGuard);

router.beforeEach((to, from, next) => {
	if (
		to.query.stations === undefined &&
		from.query.stations !== undefined &&
		from.query.stations !== ""
	) {
		next({ ...to, query: { ...to.query, stations: from.query.stations } });
	} else {
		next();
	}
});

router.afterEach((to) => {
	document.title = `${to.meta.title ? `${to.meta.title} — ` : ""}Audio Manager Online`;
});

export { router, routes };
